import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";

type FieldType = 'text' | 'textarea' | 'email' | 'phone' | 'boolean' | 'rating' | 'select' | 'hidden';

interface FieldDefinition {
  key: string;
  type: FieldType;
  label: string;
  required: boolean;
  options?: string[];
  position: number;
}

interface FormSettings {
  success_message?: string;
}

interface PublicFormView {
  id: number;
  name: string;
  slug: string;
  fields: FieldDefinition[];
  settings: FormSettings;
}

export default function PublicFormPage() {
  const { slug } = useParams<{ slug: string }>();
  const [form, setForm] = useState<PublicFormView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [values, setValues] = useState<Record<string, any>>({});
  const [website, setWebsite] = useState(""); // Honeypot
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchForm = async () => {
      try {
        const res = await fetch(`/public/forms/${slug}`);
        const data = await res.json();
        if (data.ok) {
          setForm(data.form);
          // Initialize default values
          const searchParams = new URLSearchParams(window.location.search);
          const initialValues: Record<string, any> = {};
          data.form.fields.forEach((f: FieldDefinition) => {
            if (f.type === 'boolean') initialValues[f.key] = searchParams.get(f.key) === 'true';
            else if (f.type === 'rating') initialValues[f.key] = parseInt(searchParams.get(f.key) || '0', 10);
            else initialValues[f.key] = searchParams.get(f.key) || "";
          });
          setValues(initialValues);
        } else {
          setError("Form non trovato o non disponibile.");
        }
      } catch (err) {
        setError("Impossibile caricare il form.");
      } finally {
        setLoading(false);
      }
    };
    fetchForm();
  }, [slug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationErrors({});
    setSubmitting(true);

    try {
      const res = await fetch(`/public/forms/${slug}/submissions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ values, website }),
      });
      const data = await res.json();
      
      if (data.ok) {
        setSuccess(true);
      } else {
        if (data.errors) {
          const errs: Record<string, string> = {};
          data.errors.forEach((err: any) => {
            const errorMessages: Record<string, string> = {
              field_required: "Campo obbligatorio",
              invalid_phone: "Numero non valido",
              invalid_email: "Email non valida",
              invalid_boolean: "Seleziona Sì o No",
              invalid_rating: "Seleziona una valutazione",
            };
            errs[err.field] = err.message || errorMessages[err.code] || err.code || "Campo obbligatorio";
          });
          setValidationErrors(errs);
        } else {
          setError(data.error || "Invio fallito. Riprova.");
        }
      }
    } catch (err) {
      setError("Si è verificato un errore imprevisto.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (key: string, value: any) => {
    setValues(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-fuchsia-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !form) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-8 max-w-md w-full text-center">
          <h1 className="text-xl font-bold text-white mb-2">Error</h1>
          <p className="text-zinc-400">{error}</p>
        </div>
      </div>
    );
  }

  if (success) {
    const handleNewForm = () => {
      setSuccess(false);
      const initialValues: Record<string, unknown> = {};
      form?.fields.forEach((f) => {
        if (f.type === 'boolean') initialValues[f.key] = undefined;
        else if (f.type === 'rating') initialValues[f.key] = 0;
        else initialValues[f.key] = '';
      });
      setValues(initialValues);
      setValidationErrors({});
      setError("");
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
            <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Inviato!</h1>
          <p className="text-zinc-400 mb-6">
            {form?.settings?.success_message || "La tua risposta è stata inviata con successo."}
          </p>
          <button
            onClick={handleNewForm}
            className="px-6 py-3 bg-fuchsia-600 hover:bg-fuchsia-500 text-white rounded-xl font-medium transition-colors"
          >
            Compila nuovo modulo
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-xl"
      >
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white">{form?.name}</h1>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-zinc-900/80 backdrop-blur-xl border border-zinc-800 rounded-2xl p-6 sm:p-8 space-y-6"
        >
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Honeypot field */}
          <div className="hidden" aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input
              type="text"
              id="website"
              name="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {form?.fields.sort((a, b) => a.position - b.position).map((field) => {
            if (field.type === 'hidden') return null;

            return (
              <div key={field.key} className="space-y-2">
                <label className="block text-sm font-medium text-zinc-300">
                  {field.label} {field.required && <span className="text-fuchsia-500">*</span>}
                </label>
                
                {field.type === 'text' || field.type === 'email' || field.type === 'phone' ? (
                  <input
                    type={field.type === 'phone' ? 'tel' : field.type}
                    value={values[field.key] || ""}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    required={field.required}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-fuchsia-500 focus:ring-1 focus:ring-fuchsia-500 transition-all"
                  />
                ) : field.type === 'textarea' ? (
                  <textarea
                    value={values[field.key] || ""}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    required={field.required}
                    rows={4}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-fuchsia-500 focus:ring-1 focus:ring-fuchsia-500 transition-all resize-none"
                  />
                ) : field.type === 'select' ? (
                  <select
                    value={values[field.key] || ""}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    required={field.required}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-fuchsia-500 focus:ring-1 focus:ring-fuchsia-500 transition-all appearance-none"
                  >
                    <option value="" disabled>Seleziona un'opzione</option>
                    {field.options?.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : field.type === 'boolean' ? (
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name={field.key}
                        checked={values[field.key] === true}
                        onChange={() => handleChange(field.key, true)}
                        required={field.required && values[field.key] === undefined}
                        className="w-5 h-5 border-zinc-700 bg-zinc-950 text-fuchsia-500 focus:ring-fuchsia-500 focus:ring-offset-zinc-900"
                      />
                      <span className="text-zinc-300">Sì</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name={field.key}
                        checked={values[field.key] === false}
                        onChange={() => handleChange(field.key, false)}
                        className="w-5 h-5 border-zinc-700 bg-zinc-950 text-fuchsia-500 focus:ring-fuchsia-500 focus:ring-offset-zinc-900"
                      />
                      <span className="text-zinc-300">No</span>
                    </label>
                  </div>
                ) : field.type === 'rating' ? (
                  <div className="flex items-center space-x-2">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => handleChange(field.key, star)}
                        className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg transition-colors ${
                          (values[field.key] || 0) >= star 
                            ? 'bg-fuchsia-500/20 text-fuchsia-500 border border-fuchsia-500/50' 
                            : 'bg-zinc-950 border border-zinc-800 text-zinc-600 hover:border-zinc-700'
                        }`}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                ) : null}

                {validationErrors[field.key] && (
                  <p className="text-red-400 text-sm mt-1">{validationErrors[field.key]}</p>
                )}
              </div>
            );
          })}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-gradient-to-r from-fuchsia-600 to-orange-500 hover:from-fuchsia-500 hover:to-orange-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3.5 rounded-xl transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] mt-8"
          >
            {submitting ? "Invio in corso..." : "Invia"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
